'use client';

import { createLogger } from '@svs/nova-core/log';

import type { WidgetStateType, WidgetTheme } from '../../providers/sportradar/entry.js';
import { clearTimer, getWidgetClassByMatchId, setupWidgetReference, startSirScript, startTimer } from './sir.js';

const logger = createLogger('poolbetting.sportinfo.sportradar');

const THEME_CLASS_BY_THEME: Record<WidgetTheme, string> = {
  LIGHT: 'sportradar-theme-light',
  DARK: 'sportradar-theme-dark',
  AUTO: 'sportradar-theme-auto',
};

const getThemeClass = (theme: WidgetTheme): string => THEME_CLASS_BY_THEME[theme];

const buildWidgetClassName = (matchId: string | number, widgetType: string, theme: WidgetTheme): string =>
  [getWidgetClassByMatchId(matchId, widgetType, false), 'sr-sir-widget', getThemeClass(theme)].join(' ');

const toError = (error: unknown): Error =>
  error instanceof Error ? error : new Error(`Unknown error: ${String(error)}`);

const mountSirScript = async (): Promise<void> => {
  if (!window.SIR) {
    await startSirScript();
  }
};

const logError = (err: Error): void => {
  logger.error('Error in SportradarInitializer', { error: err });
};

const isDead = (state: WidgetStateType): boolean => state === 'Dead' || state === 'Error';

export type CreateWidgetArgs<TWidgetProps extends Record<string, unknown> = Record<string, unknown>> = {
  matchId?: string | number;
  widgetType: string;
  widgetProps?: TWidgetProps;
  element?: HTMLElement;
  theme?: WidgetTheme;
  onStateChange?: (state: WidgetStateType) => void;
};

export type CreatedWidget = {
  remove: () => void;
  tryStart: () => void;
};

/**
 * Creates a Sportradar widget.
 *
 * @param props - The arguments for creating the widget
 * @param props.matchId - The ID of the match to associate with the widget
 * @param props.widgetType - The type of the widget to create
 * @param props.widgetProps - Additional properties for the widget
 * @param props.element - The HTML element where the widget will be mounted
 * @param props.theme - The theme of the widget (DARK, LIGHT, AUTO)
 * @param props.onStateChange - Callback function to handle state changes of the widget
 * @returns The created widget with methods to remove and start it
 * @throws {Error} Will throw an error if the widget cannot be started due to invalid parameters or other issues
 */
export function createWidget<TWidgetProps extends Record<string, unknown> = Record<string, unknown>>({
  matchId,
  widgetType,
  widgetProps,
  element,
  theme = 'DARK',
  onStateChange,
}: CreateWidgetArgs<TWidgetProps>): CreatedWidget {
  if (matchId === undefined || matchId === '') {
    const errMsg = 'Cannot start widget without matchId';
    throw new Error(errMsg);
  }

  if (!element) {
    const errMsg = 'Cannot start widget without a parent element';
    throw new Error(errMsg);
  }

  let el: HTMLDivElement | undefined;
  let state: WidgetStateType = 'Undefined';
  let runId = 0;
  const isCancelled = (id: number): boolean => id !== runId;
  const shouldAbort = (id: number): boolean => isDead(state) || isCancelled(id);

  const setState = (newState: WidgetStateType): void => {
    if (isDead(state)) {
      return;
    }

    state = newState;

    if (isDead(newState)) {
      clearTimer(matchId, widgetType);

      if (el) {
        window.SIR?.('removeWidget', el);
        el.remove();
        el = undefined;
      }
    }

    onStateChange?.(newState);
  };

  const createWidgetElement = (): void => {
    el = document.createElement('div');

    el.className = buildWidgetClassName(matchId, widgetType, theme);
  };

  const addWidgetByElement = async (myRunId: number): Promise<void> => {
    if (isDead(state) || !el) {
      return;
    }

    setupWidgetReference(matchId, widgetType, el);

    const sirWidgetProps = { ...(widgetProps ?? ({} as TWidgetProps)), matchId };

    // Avoid hanging forever if the SIR callback never fires.
    // Avoid calling addWidget if SIR is unexpectedly missing.
    await Promise.race([
      new Promise<void>((resolve, reject) => {
        if (!window.SIR) {
          reject(new Error('SIR is not available when trying to addWidget'));
          return;
        }

        window.SIR('addWidget', el, widgetType, sirWidgetProps, () => {
          resolve();
        });
      }),
      new Promise<void>((_, reject) => {
        const intervalId = window.setInterval(() => {
          if (isCancelled(myRunId) || isDead(state)) {
            window.clearInterval(intervalId);
            reject(new Error('Cancelled while waiting for SIR addWidget callback'));
          }
        }, 50);
      }),
      new Promise<void>((_, reject) => {
        window.setTimeout(() => {
          reject(new Error('Timed out waiting for SIR addWidget callback'));
        }, 10_000);
      }),
    ]);
  };

  const mountWidgetToDOM = (): void => {
    if (!el) {
      return;
    }

    element.append(el);
  };

  return {
    remove: (): void => {
      runId += 1;
      setState('Dead');
    },
    tryStart: (): void => {
      void (async (): Promise<void> => {
        try {
          runId += 1;
          const myRunId = runId;
          setState('Loading');

          await mountSirScript();
          if (shouldAbort(myRunId)) {
            return;
          }

          createWidgetElement();
          if (shouldAbort(myRunId)) {
            return;
          }

          await addWidgetByElement(myRunId);
          if (shouldAbort(myRunId)) {
            return;
          }

          mountWidgetToDOM();
          if (shouldAbort(myRunId)) {
            return;
          }

          startTimer(matchId, widgetType, widgetProps);
          setState('Alive');
        } catch (error: unknown) {
          const currentError = toError(error);
          setState('Error');
          logError(currentError);
        }
      })();
    },
  };
}
